import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiBriefcase } from 'react-icons/fi';
import api from '../services/api';
import './GruposEmpresariais.css';

const GruposEmpresariais = () => {
  const [grupos, setGrupos] = useState([]);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nome: '', descricao: '', empreendimento_ids: [] });

  const carregarGrupos = useCallback(async () => {
    try {
      const res = await api.get('/api/grupos-empresariais');
      setGrupos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erro ao carregar grupos:', err);
      setGrupos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const carregarEmpreendimentos = useCallback(async () => {
    try {
      const res = await api.get('/api/empreendimentos');
      const data = res.data;
      const list = data?.data ?? (Array.isArray(data) ? data : []);
      setEmpreendimentos(list.filter((e) => e.nome && !['Empreendimento (a definir)', 'Empreendimento (PDF sem extração)'].includes(e.nome)));
    } catch (err) {
      console.error('Erro ao carregar empreendimentos:', err);
      setEmpreendimentos([]);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    carregarGrupos();
    carregarEmpreendimentos();
  }, [carregarGrupos, carregarEmpreendimentos]);

  const abrirNovo = () => {
    setEditing(null);
    setForm({ nome: '', descricao: '', empreendimento_ids: [] });
    setShowModal(true);
  };

  const abrirEditar = async (grupo) => {
    setEditing(grupo.id);
    try {
      const res = await api.get(`/api/grupos-empresariais/${grupo.id}`);
      setForm({
        nome: res.data.nome || '',
        descricao: res.data.descricao || '',
        empreendimento_ids: res.data.empreendimento_ids || []
      });
      setShowModal(true);
    } catch (err) {
      console.error('Erro ao carregar grupo:', err);
      alert('Erro ao carregar grupo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/api/grupos-empresariais/${editing}`, {
          nome: form.nome.trim(),
          descricao: form.descricao.trim() || undefined,
          empreendimento_ids: form.empreendimento_ids
        });
      } else {
        await api.post('/api/grupos-empresariais', {
          nome: form.nome.trim(),
          descricao: form.descricao.trim() || undefined,
          empreendimento_ids: form.empreendimento_ids
        });
      }
      setShowModal(false);
      carregarGrupos();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao salvar grupo');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir este grupo? Os empreendimentos não serão excluídos, apenas o agrupamento.')) return;
    try {
      await api.delete(`/api/grupos-empresariais/${id}`);
      carregarGrupos();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao excluir');
    }
  };

  const toggleEmpreendimento = (empId) => {
    setForm((prev) => ({
      ...prev,
      empreendimento_ids: prev.empreendimento_ids.includes(empId)
        ? prev.empreendimento_ids.filter((id) => id !== empId)
        : [...prev.empreendimento_ids, empId]
    }));
  };

  if (loading) {
    return (
      <div className="grupos-empresariais-page">
        <div className="loading">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="grupos-empresariais-page">
      <div className="page-header">
        <h1 className="page-title">
          <FiBriefcase className="page-title-icon" />
          Grupos empresariais
        </h1>
        <button type="button" className="btn btn-primary" onClick={abrirNovo}>
          <FiPlus /> Novo grupo
        </button>
      </div>

      <p className="grupos-desc">
        Agrupe vários empreendimentos em um grupo empresarial para organização e relatórios.
      </p>

      {grupos.length === 0 ? (
        <div className="empty-state">
          <p>Nenhum grupo cadastrado.</p>
          <button type="button" className="btn btn-primary" onClick={abrirNovo}>
            <FiPlus /> Criar primeiro grupo
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Descrição</th>
                <th>Empreendimentos</th>
                <th style={{ width: 120 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {grupos.map((g) => (
                <tr key={g.id}>
                  <td><strong>{g.nome}</strong></td>
                  <td className="text-muted">{g.descricao || '—'}</td>
                  <td>{g.empreendimentos_count ?? 0}</td>
                  <td>
                    <div className="action-buttons">
                      <button type="button" className="btn btn-sm btn-outline" onClick={() => abrirEditar(g)} title="Editar">
                        <FiEdit2 />
                      </button>
                      <button type="button" className="btn btn-sm btn-outline btn-danger" onClick={() => handleDelete(g.id)} title="Excluir">
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="modal-content grupos-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editing ? <><FiEdit2 /> Editar</> : <><FiPlus /> Novo</>} grupo empresarial</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  required
                  value={form.nome}
                  onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))}
                  placeholder="Ex: Grupo Sul"
                />
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <input
                  type="text"
                  value={form.descricao}
                  onChange={(e) => setForm((prev) => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Opcional"
                />
              </div>
              <div className="form-group">
                <label>Empreendimentos do grupo</label>
                <div className="grupos-empresarios-checklist">
                  {empreendimentos.length === 0 ? (
                    <p className="text-muted">Nenhum empreendimento disponível.</p>
                  ) : (
                    empreendimentos.map((emp) => (
                      <label key={emp.id} className="grupos-checklist-item">
                        <input
                          type="checkbox"
                          checked={form.empreendimento_ids.includes(emp.id)}
                          onChange={() => toggleEmpreendimento(emp.id)}
                        />
                        <span>{emp.nome}</span>
                        {emp.municipio && <span className="text-muted"> — {emp.municipio}</span>}
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => !saving && setShowModal(false)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Salvando…' : editing ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GruposEmpresariais;
